import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { AxiosError } from 'axios'
import type { Conversation } from '@/types/chatwoot'
import {
  fetchAllConversationsBoot,
  fetchConversation,
  fetchConversationsPage,
} from '@/api/conversations'
import { resolveStage, type StageKey } from '@/config/stages'
import { useInboxesStore } from './inboxes'
import { useKanbanConfigStore } from './kanban-config'
import { useAuthStore } from './auth'

const PAGE_SIZE = 25
const CONCURRENT_BATCH = 3
const ASSIGNED_ONLY_STORAGE_KEY = 'kanban.assignedOnlyView.v1'

export const useConversationsStore = defineStore('conversations', () => {
  const items = ref<Conversation[]>([])
  const total = ref(0)
  const loadingInitial = ref(false)
  const loadingMore = ref(false)
  const refreshing = ref(false)
  const error = ref<string | null>(null)
  // IDs com PATCH em andamento. O polling pula esses para não sobrescrever
  // estado otimista que ainda não foi confirmado pelo servidor.
  const pendingMutations = ref<Set<number>>(new Set())

  // Preferência local do supervisor/admin para focar apenas nos cards
  // atribuídos a si. Default false (vê todos). Persistido em localStorage.
  const assignedOnlyView = ref<boolean>(false)

  function persistAssignedOnly() {
    if (assignedOnlyView.value) {
      localStorage.setItem(ASSIGNED_ONLY_STORAGE_KEY, '1')
    } else {
      localStorage.removeItem(ASSIGNED_ONLY_STORAGE_KEY)
    }
  }

  function hydrateAssignedOnly(): void {
    assignedOnlyView.value =
      localStorage.getItem(ASSIGNED_ONLY_STORAGE_KEY) === '1'
  }

  function setAssignedOnly(value: boolean): void {
    assignedOnlyView.value = value
    persistAssignedOnly()
  }

  const loaded = computed(() => items.value.length)
  const progress = computed(() => {
    if (total.value === 0) return 0
    return Math.min(100, Math.round((loaded.value / total.value) * 100))
  })
  const isLoading = computed(
    () => loadingInitial.value || loadingMore.value,
  )

  // O push_event_data do Chatwoot (ActionCable) NAO traz `assignee_id` top-level
  // — so `meta.assignee.id`. Mas o store/filter usa `c.assignee_id`. Sem essa
  // normalizacao, o primeiro broadcast que toca uma conversation sobrescreve o
  // objeto e zera o `assignee_id`, fazendo o card sumir em stages assigned_only
  // (bug observado 2026-05-19 com a Fernanda Mendes movendo cards pra
  // "Em atendimento"). Aplicado em upsertMany para cobrir tanto load REST
  // (que ja traz top-level) quanto push events (que nao trazem).
  function normalizeAssignee(conv: Conversation): Conversation {
    if (conv.assignee_id !== undefined) return conv
    return { ...conv, assignee_id: conv.meta?.assignee?.id ?? null }
  }

  // Indexa por id para inserções idempotentes durante o carregamento concorrente.
  function upsertMany(batch: Conversation[]) {
    const map = new Map(items.value.map((c) => [c.id, c]))
    for (const conv of batch) map.set(conv.id, normalizeAssignee(conv))
    items.value = Array.from(map.values())
  }

  // Como upsertMany, mas pula conversations com PATCH otimista pendente para
  // não sobrescrever mudanças locais ainda não confirmadas pelo servidor.
  function upsertManyRespectingPending(batch: Conversation[]) {
    if (pendingMutations.value.size === 0) {
      upsertMany(batch)
      return
    }
    const filtered = batch.filter((c) => !pendingMutations.value.has(c.id))
    upsertMany(filtered)
  }

  function markPending(id: number) {
    pendingMutations.value.add(id)
  }

  function unmarkPending(id: number) {
    pendingMutations.value.delete(id)
  }

  // Contagem por inbox (sempre sobre o total, ignora filtro — usado para
  // mostrar a quantidade ao lado de cada chip no ChannelFilter).
  const inboxCounts = computed(() => {
    const counts = new Map<number, number>()
    for (const c of items.value) {
      counts.set(c.inbox_id, (counts.get(c.inbox_id) ?? 0) + 1)
    }
    return counts
  })

  // Aplica filtro de inboxes + regras de visibilidade por stage (Fase 2).
  // Cascata:
  //   1. Privilegiado (admin OU supervisor extra) sem toggle "apenas meus" → vê tudo
  //   2. Privilegiado COM toggle "apenas meus" → filtra só pelo assignee_id
  //      (mantém acesso a colunas supervisors_only — toggle não rebaixa)
  //   3. Agente comum: stage visibility=supervisors_only → oculta tudo;
  //      visibility=all → todos veem; visibility=assigned_only → só assignee
  const filteredItems = computed(() => {
    const inboxes = useInboxesStore()
    const config = useKanbanConfigStore()
    const auth = useAuthStore()

    const base = inboxes.allSelected
      ? items.value
      : items.value.filter((c) => inboxes.isVisible(c.inbox_id))

    const isPrivileged =
      auth.user?.role === 'administrator' ||
      (auth.user
        ? config.supervisorUserIds.includes(auth.user.id)
        : false)
    const userId = auth.user?.id

    if (isPrivileged && !assignedOnlyView.value) return base

    if (isPrivileged && assignedOnlyView.value) {
      return base.filter((c) => userId != null && c.assignee_id === userId)
    }

    return base.filter((c) => {
      const stageKey = resolveStage(
        c.custom_attributes?.kanban_stage,
        config.stages,
      )
      const stage = config.stages.find((s) => s.key === stageKey)
      const visibility = stage?.visibility ?? 'all'
      if (visibility === 'supervisors_only') return false
      if (visibility === 'all') return true
      // assigned_only
      return userId != null && c.assignee_id === userId
    })
  })

  const groupedByStage = computed(() => {
    const config = useKanbanConfigStore()
    const keys = config.stages.map((s) => s.key)
    const groups = Object.fromEntries(
      keys.map((k) => [k, [] as Conversation[]]),
    ) as Record<StageKey, Conversation[]>
    for (const c of filteredItems.value) {
      const stage = resolveStage(
        c.custom_attributes?.kanban_stage,
        config.stages,
      )
      if (!groups[stage]) groups[stage] = []
      groups[stage].push(c)
    }
    // Ordena cada coluna por last_activity_at DESC. Sem isso, a posicao do
    // card na coluna herdava da ordem global do array `items` (definida no
    // boot pelo SQL `ORDER BY last_activity_at DESC`) e ficava staleness
    // visual quando push events de message.created atualizavam o timestamp
    // sem reposicionar — agora qualquer recompute reordena.
    for (const k of keys) {
      groups[k].sort(
        (a, b) => (b.last_activity_at ?? 0) - (a.last_activity_at ?? 0),
      )
    }
    return groups
  })

  function setErrorFrom(e: unknown, fallback: string) {
    const axiosError = e as AxiosError<{ errors?: string[]; message?: string }>
    const data = axiosError.response?.data
    error.value =
      data?.errors?.[0] ?? data?.message ?? axiosError.message ?? fallback
  }

  // Boot via kanban-api (1 SQL no Postgres) — ordens de magnitude mais
  // rapido que paginar o REST. Se VITE_KANBAN_USE_DB_BOOTSTRAP=false OU
  // o endpoint falhar, cai para `loadViaPagination()` (caminho legado).
  async function load(): Promise<void> {
    if (loadingInitial.value || loadingMore.value) return
    loadingInitial.value = true
    error.value = null
    items.value = []
    total.value = 0

    const useDbBootstrap =
      import.meta.env.VITE_KANBAN_USE_DB_BOOTSTRAP !== 'false'

    if (useDbBootstrap) {
      try {
        const result = await fetchAllConversationsBoot()
        total.value = result.meta.all_count
        upsertMany(result.conversations)
        loadingInitial.value = false
        return
      } catch (e) {
        console.warn(
          '[conversations] bootstrap kanban-api falhou, caindo para pagination REST:',
          e,
        )
        // Continua para o fallback abaixo.
      }
    }

    await loadViaPagination()
  }

  // Caminho legado: pagina o REST do Chatwoot. Usado como fallback do
  // load() e como base do refresh() (que ainda precisa varrer paginas
  // para capturar mudancas em custom_attributes que nao bumpam o
  // last_activity_at — mas refresh() so roda quando o ActionCable cai).
  async function loadViaPagination(): Promise<void> {
    try {
      const first = await fetchConversationsPage({ page: 1 })
      total.value = first.meta.all_count
      upsertMany(first.conversations)
      loadingInitial.value = false

      const totalPages = Math.ceil(total.value / PAGE_SIZE)
      if (totalPages <= 1) return

      loadingMore.value = true
      const queue: number[] = []
      for (let p = 2; p <= totalPages; p++) queue.push(p)

      while (queue.length > 0) {
        const batch = queue.splice(0, CONCURRENT_BATCH)
        const results = await Promise.all(
          batch.map((p) =>
            fetchConversationsPage({ page: p }).catch((e) => {
              // Falha em página individual não aborta o lote inteiro
              console.warn(`[conversations] page ${p} falhou:`, e)
              return null
            }),
          ),
        )
        for (const r of results) {
          if (r) upsertMany(r.conversations)
        }
      }
    } catch (e) {
      setErrorFrom(e, 'Falha ao carregar conversations')
    } finally {
      loadingInitial.value = false
      loadingMore.value = false
    }
  }

  // Refresh disparado pelo polling. Varre TODAS as páginas (mesma estratégia do
  // load(), com concorrência), pois mudanças em custom_attributes não mexem em
  // last_activity_at — então restringir o refresh à página 1 perdia atualizações
  // em conversas fora do top 25 (bug observado 2026-05-14: edição via Chatwoot
  // UI só aparecia no Kanban após reload da página).
  async function refresh(): Promise<void> {
    if (loadingInitial.value || refreshing.value) return
    refreshing.value = true
    try {
      const first = await fetchConversationsPage({ page: 1 })
      total.value = first.meta.all_count
      upsertManyRespectingPending(first.conversations)

      const totalPages = Math.ceil(total.value / PAGE_SIZE)
      if (totalPages <= 1) return

      const queue: number[] = []
      for (let p = 2; p <= totalPages; p++) queue.push(p)
      while (queue.length > 0) {
        const batch = queue.splice(0, CONCURRENT_BATCH)
        const results = await Promise.all(
          batch.map((p) =>
            fetchConversationsPage({ page: p }).catch((e) => {
              console.warn(`[conversations] refresh page ${p} falhou:`, e)
              return null
            }),
          ),
        )
        for (const r of results) {
          if (r) upsertManyRespectingPending(r.conversations)
        }
      }
    } catch (e) {
      // Silenciamos erros de polling — não queremos um banner vermelho a cada
      // 20s caso a conexão oscile. Logamos no console e seguimos.
      console.warn('[conversations] refresh falhou:', e)
    } finally {
      refreshing.value = false
    }
  }

  // Re-fetch de UMA conversa. Usado quando o usuário abre o drawer de um card,
  // para garantir que o conteúdo exibido reflete o estado mais recente no
  // servidor (vs. esperar o próximo tick do polling).
  async function refreshOne(id: number): Promise<void> {
    if (pendingMutations.value.has(id)) return
    try {
      const conv = await fetchConversation(id)
      upsertMany([conv])
    } catch (e) {
      console.warn(`[conversations] refreshOne(${id}) falhou:`, e)
    }
  }

  // Update otimista para mover card entre colunas. Retorna o snapshot dos
  // atributos antigos + os novos atributos a serem enviados ao Chatwoot,
  // além de uma função de rollback que reaplica o estado anterior.
  function applyStage(
    conversationId: number,
    stage: StageKey,
    changedBy: string,
  ): {
    newAttrs: Record<string, unknown>
    rollback: () => void
  } | null {
    const idx = items.value.findIndex((c) => c.id === conversationId)
    if (idx === -1) return null
    const current = items.value[idx]
    const previousAttrs = { ...current.custom_attributes }
    const newAttrs = {
      ...current.custom_attributes,
      kanban_stage: stage,
      kanban_stage_changed_at: new Date().toISOString(),
      kanban_stage_changed_by: changedBy,
    }
    items.value.splice(idx, 1, {
      ...current,
      custom_attributes: newAttrs,
    })
    return {
      newAttrs,
      rollback: () => {
        const i = items.value.findIndex((c) => c.id === conversationId)
        if (i === -1) return
        items.value.splice(i, 1, {
          ...items.value[i],
          custom_attributes: previousAttrs,
        })
      },
    }
  }

  // Update otimista genérico para qualquer subset de custom_attributes.
  // Usado pelos campos editáveis do drawer (service_value, service_description,
  // internal_notes). Mesma semântica do applyStage: snapshot prévio + rollback.
  function applyAttrs(
    conversationId: number,
    partial: Record<string, unknown>,
  ): {
    newAttrs: Record<string, unknown>
    rollback: () => void
  } | null {
    const idx = items.value.findIndex((c) => c.id === conversationId)
    if (idx === -1) return null
    const current = items.value[idx]
    const previousAttrs = { ...current.custom_attributes }
    const newAttrs = {
      ...current.custom_attributes,
      ...partial,
    }
    items.value.splice(idx, 1, {
      ...current,
      custom_attributes: newAttrs,
    })
    return {
      newAttrs,
      rollback: () => {
        const i = items.value.findIndex((c) => c.id === conversationId)
        if (i === -1) return
        items.value.splice(i, 1, {
          ...items.value[i],
          custom_attributes: previousAttrs,
        })
      },
    }
  }

  // Aplica um evento de push (ActionCable) que carrega a conversation
  // completa. Se o status nao for 'open' (resolved/snoozed/pending), remove
  // do board — caso contrario, upsert respeitando pendingMutations.
  function applyPushEvent(conv: Conversation): void {
    if (conv.status !== 'open') {
      removeOne(conv.id)
      return
    }
    upsertManyRespectingPending([conv])
  }

  function removeOne(id: number): void {
    const idx = items.value.findIndex((c) => c.id === id)
    if (idx !== -1) items.value.splice(idx, 1)
  }

  // Atualiza last_activity_at de uma conversation sem refetch — usado pelo
  // evento message.created, que so traz a mensagem (nao a conversation
  // completa). Se a conv nao existe no store, ignora silenciosamente.
  function touchActivity(conversationId: number, timestamp: number): void {
    const idx = items.value.findIndex((c) => c.id === conversationId)
    if (idx === -1) return
    if (pendingMutations.value.has(conversationId)) return
    const current = items.value[idx]
    if (current.last_activity_at && current.last_activity_at >= timestamp) return
    items.value.splice(idx, 1, { ...current, last_activity_at: timestamp })
  }

  function $reset() {
    items.value = []
    total.value = 0
    loadingInitial.value = false
    loadingMore.value = false
    error.value = null
  }

  return {
    items,
    total,
    loaded,
    progress,
    loadingInitial,
    loadingMore,
    refreshing,
    isLoading,
    error,
    pendingMutations,
    inboxCounts,
    filteredItems,
    groupedByStage,
    assignedOnlyView,
    hydrateAssignedOnly,
    setAssignedOnly,
    load,
    refresh,
    refreshOne,
    applyStage,
    applyAttrs,
    applyPushEvent,
    removeOne,
    touchActivity,
    markPending,
    unmarkPending,
    setError: (msg: string | null) => {
      error.value = msg
    },
    $reset,
  }
})
